/**
 * 净资产唯一权威 —— `composeNetWorth()`
 *
 * 依据：规格 **§12**（净资产构成，权威口径）/ **§22.7** · 架构 **§5.4**（资产 10 类 / 负债 6 类）
 * · `DR-008`（`internal` 净额入表）· `DR-012`（P0 即全量口径）· **`DR-026`**（负债方 6 类定稿）。
 *
 * ## 六条实现铁律（架构 §5.4，逐条在代码中兑现）
 *
 * 1. **P0 即全量口径** —— 无"缩窄期"、不写过渡文案；
 * 2. **`internal` 净额入表**（不拆两行）：余额 < 0 入负债侧、> 0 入资产侧；
 *    仅正品类（`prepaid` / `amortize`）与可用型 `social_*` **恒入资产方**；
 * 3. 🔴 **`insurance_sav` 是唯一入表例外** —— 余额仅展示、**不入净资产**
 *    （防同一笔钱双计 + 防早期退保高估家底）；
 * 4. **前后端同源纯函数**；实时派生、**永不落库**；
 * 5. **多币种**：逐原币 × 汇率 → 本币分，**保留原币值**；
 *    缺汇率产出 `fxWarnings` —— 🔴 **不静默、不按 1:1 冒充**（缺汇率项 `baseCents = null` 且不计入总额）；
 * 6. **边界全部在函数内处理**（`>0` / `<0` / `hidden` / 占位行）—— **调用方不得二次加工**。
 *
 * ## 关于"不静默"的三类附加输出（本实现的取舍，非文档原话）
 *
 * | 输出 | 用途 |
 * |---|---|
 * | `fxWarnings` | 缺汇率（铁律 5 明确要求） |
 * | `notCounted` | **有意不计入**但需向用户说明的项（`insurance_sav` / 养老类社保）—— 避免用户以为"钱丢了" |
 * | `anomalies` | **数据异常**（负的货币资金余额 / 负的仅正品类 / 未知品类或 kind）—— 只报告、不修数 |
 *
 * ⚠️ 三者的共同纪律：**绝不静默吞掉**（沿用本项目"清污染必须实测"的一贯做法）。
 */

import type { Cents, RateE4 } from '../types/brand.js';
import { AccountType, ContraKind, InternalCategory } from '../enums/account.js';
import { sumCents, toCents } from './money.js';
import { buildFxIndex, convertToBase, type FxIndex, type FxRate, type FxWarning } from './fx.js';

// ───────────────────────────── 输入 ─────────────────────────────

/** 一个账户的余额快照（余额**由腿派生**：`初始余额 + Σin − Σout`，架构 §4.1） */
export interface AccountBalance {
  id: string;
  type: AccountType;
  /**
   * `type = 'internal'` 时为 `InternalCategory`；`type = 'contra'` 时为 `ContraKind`；
   * 其余类型为 `null`（架构 §4.2：该列由 `type` 解释语义）。
   */
  category: string | null;
  /** 原币 ISO 代码（🔴 腿币种继承账户，不单挂 `currency` 列） */
  currency: string;
  /** **原币**整数分（可正可负） */
  balance: Cents;
  /** 隐藏的账户不计入净资产（J 域 `E6`：前端与服务端**均**过滤） */
  hidden: boolean;
}

/**
 * 一项**非腿派生**的估值项（持仓市值 / 重大资产 / 手动资产 / 手动负债 / 保单现金价值 / 分期未到期本金）。
 *
 * 🔴 负债类项（手动负债 / 分期未到期本金）**请传正数**，函数按负债侧处理；
 * 传负数会被取绝对值并在 `anomalies` 中记录一条。
 */
export interface ValuationItem {
  id: string;
  /** **原币**整数分 */
  amount: Cents;
  currency: string;
  /** 忽略该项（J 域 `E6`） */
  hidden?: boolean;
  /**
   * 「投资理财」类**占位行**（J 域 `E5`）—— 手动资产里与投资模块双计的来源，
   * 必须剔除。语义由调用方标注（识别规则属数据层），**过滤动作在函数内**（铁律 6）。
   */
  isPlaceholder?: boolean;
}

export interface ComposeNetWorthInput {
  /** 账本本币（`ledgers.baseCurrency`） */
  baseCurrency: string;
  accounts: readonly AccountBalance[];
  /** 投资持仓市值 —— 🔴 **与 `investment` 账户余额分列不合并** */
  holdings?: readonly ValuationItem[];
  /** 重大资产（全额；关联房贷在负债侧，资产侧不再扣减 —— 防双计） */
  majorAssets?: readonly ValuationItem[];
  /** 手动资产 */
  manualAssets?: readonly ValuationItem[];
  /** 手动负债 */
  manualLiabilities?: readonly ValuationItem[];
  /** 保单现金价值（🔴 限 `status ∈ {active, paid_up}`；**保额不计**） */
  policyCashValues?: readonly ValuationItem[];
  /** 信用卡分期未到期本金（🔴 **仅 `pending` 且未生成流水的期次**） */
  installmentPendingPrincipal?: readonly ValuationItem[];
  /**
   * 🆕 信用卡分期未到期本金，**按账户合计**（`DR-060` §3 · `AC-B2.12`）。
   *
   * 🔴 **成对换分类的唯一数据源** —— `credit` 桶 = `min(0, 卡余额 + Σ pending 本金)`。
   * 只做一边必错（两个方向的错法都已演算，见 `DR-060` §3）：
   * 只加不减 ⇒ 多算；只减不加 ⇒ 分期与还款一旦脱节立刻算错。
   * 代价：卡面「本期应还」不再等于账户余额，**必须分三块显示**。
   */
  installmentPendingByAccount?: readonly {
    accountId: string;
    /** Σ pending 期次本金（正数，整数分；**不含手续费**） */
    amount: Cents;
    currency: string;
  }[];
  fx?: readonly FxRate[];
}

// ───────────────────────────── 输出 ─────────────────────────────

/** 分桶明细的一行（**保留原币值**，铁律 5） */
export interface BucketLine {
  sourceId: string;
  /** 原币整数分（资产/负债侧均为**正数**；符号语义由所属桶承载） */
  originalCents: Cents;
  currency: string;
  /** 折算所用汇率（万分之一）；本币项 = `RATE_SCALE`；**缺汇率 = `null`** */
  rateE4: RateE4 | null;
  /** 本币分；🔴 **缺汇率时为 `null`**（不计入总额，不按 1:1 冒充） */
  baseCents: Cents | null;
}

export interface Bucket {
  key: string;
  label: string;
  /** 本币分合计 —— 🔴 **只累加能折算的部分**（`fxIncomplete = true` 时该数不完整） */
  baseCents: Cents;
  lines: readonly BucketLine[];
  /** 该桶存在缺汇率项 ⇒ 本币合计**不完整**（UI 须标注） */
  fxIncomplete: boolean;
}

/** **有意不计入**但需向用户说明的项 */
export interface NotCountedItem {
  sourceId: string;
  category: string;
  originalCents: Cents;
  currency: string;
  reason: 'insuranceSavExcluded' | 'pensionExcluded';
}

/** 数据异常（只报告、不修数） */
export interface Anomaly {
  sourceId: string;
  kind:
    | 'negativeMonetaryBalance' // 货币资金类账户余额为负（规格资产方只定义「余额 > 0」）
    | 'negativePositiveOnlyCategory' // `prepaid` / `amortize` 余额为负（仅正品类）
    | 'unknownInternalCategory'
    | 'unknownContraKind'
    | 'negativeLiabilityAmount'; // 负债类估值项传了负数
  detail: string;
}

export interface NetWorthResult {
  baseCurrency: string;
  assets: readonly Bucket[];
  liabilities: readonly Bucket[];
  totalAssets: Cents;
  totalLiabilities: Cents;
  /** `= totalAssets − totalLiabilities` */
  netWorth: Cents;
  fxWarnings: readonly FxWarning[];
  notCounted: readonly NotCountedItem[];
  anomalies: readonly Anomaly[];
}

// ───────────────────── 分桶定义（10 资产 / 6 负债） ─────────────────────

/** 资产方 **10 类**（架构 §5.4，顺序即展示顺序） */
export const ASSET_BUCKETS = [
  { key: 'monetary', label: '货币资金' },
  { key: 'creditOverpay', label: '信用卡溢缴款' },
  { key: 'receivable', label: '应收/垫付/报销' },
  { key: 'amortizing', label: '待摊未摊销' },
  { key: 'payablePositive', label: '待缴余额（预存）' },
  { key: 'socialAvailable', label: '可用型社保' },
  { key: 'policyCashValue', label: '保单现金价值' },
  { key: 'manualAsset', label: '手动资产' },
  { key: 'majorAsset', label: '重大资产' },
  { key: 'holdingsValue', label: '投资持仓市值' },
] as const;
export type AssetBucketKey = (typeof ASSET_BUCKETS)[number]['key'];

/** 负债方 **6 类**（🔴 `DR-026` 定稿：原 7 类把 `loan` 余额与 `contra(loan)` 剩余重复计了一次） */
export const LIABILITY_BUCKETS = [
  { key: 'creditDue', label: '信用卡应还' },
  { key: 'loanBalance', label: '贷款账户余额' },
  { key: 'payableRemaining', label: '应付剩余' },
  { key: 'installmentPending', label: '信用卡分期未到期本金' },
  { key: 'payableNegative', label: '待缴余额（欠付）' },
  { key: 'manualLiability', label: '手动负债' },
] as const;
export type LiabilityBucketKey = (typeof LIABILITY_BUCKETS)[number]['key'];

// ─────────────────────── 固定集合（口径落点） ───────────────────────

/** 货币资金账户类型（余额 > 0 计入资产第 1 类，**不含持仓**） */
const MONETARY_TYPES: readonly AccountType[] = [
  AccountType.Cash,
  AccountType.Debit,
  AccountType.Ewallet,
  AccountType.Investment,
];

/**
 * 🔴 **可用型社保（计资产，6 个）** —— 架构 §4.3「资产口径按险种分」。
 *
 * ⚠️ **实测发现（2026-09-28）**：架构 §5.4 资产第 6 项只列了 5 个，
 * **漏了 `social_medical`（社保侧医保）** —— 而 §4.3 判定表与 §15.2-16 均裁定
 * **「计」（依据 `Δ净资产 = 收入 − 支出` 恒等式）**。本实现**按 6 个**，
 * 已在 `docs/changelog/2026-09-28-constants-networth-expense.md` 登记该文档不一致。
 */
export const SOCIAL_AVAILABLE: readonly InternalCategory[] = [  InternalCategory.SocialMedical,
  InternalCategory.SocialMedicalCur,
  InternalCategory.SocialMedicalHist,
  InternalCategory.SocialFund,
  InternalCategory.SocialFundSup,
  InternalCategory.SocialFundVirtual,
];

/** **不计资产（3 个）** —— 退休才领或仅登记；其个人缴费**记为支出**（不是"转账不计收支"） */
export const SOCIAL_EXCLUDED: readonly InternalCategory[] = [
  InternalCategory.SocialPension,
  InternalCategory.SocialPensionSup,
  InternalCategory.SocialUnemploy,
];

/** `contra` 计入**资产侧**的 kind（应收 / 垫付 / 报销剩余本金） */
const CONTRA_ASSET_KINDS: readonly ContraKind[] = [
  ContraKind.Receivable,
  ContraKind.Advance,
  ContraKind.Reimburse,
];

// ─────────────────────────── 内部工具 ───────────────────────────

interface BucketAcc {
  baseCents: number;
  lines: BucketLine[];
  fxIncomplete: boolean;
}

function emptyAcc(): BucketAcc {
  return { baseCents: 0, lines: [], fxIncomplete: false };
}

function createAccs(defs: readonly { key: string; label: string }[]): Map<string, BucketAcc> {
  const m = new Map<string, BucketAcc>();
  for (const d of defs) m.set(d.key, emptyAcc());
  return m;
}

/** 累计一个桶的一行（含缺汇率的处理与告警收集） */
function pushLine(
  acc: BucketAcc,
  sourceId: string,
  amount: Cents,
  currency: string,
  ctx: { baseCurrency: string; fxIndex: FxIndex; fxWarnings: FxWarning[] },
): void {
  const conv = convertToBase(amount, currency, ctx.baseCurrency, ctx.fxIndex);
  if (conv === null) {
    acc.fxIncomplete = true;
    acc.lines.push({ sourceId, originalCents: amount, currency, rateE4: null, baseCents: null });
    ctx.fxWarnings.push({ sourceId, currency, reason: 'missing-rate' });
    return;
  }
  acc.baseCents += conv.baseCents;
  acc.lines.push({
    sourceId,
    originalCents: amount,
    currency,
    rateE4: conv.rateE4,
    baseCents: conv.baseCents,
  });
}

function toBucket(def: { key: string; label: string }, acc: BucketAcc): Bucket {
  return {
    key: def.key,
    label: def.label,
    baseCents: toCents(acc.baseCents) as Cents,
    lines: acc.lines,
    fxIncomplete: acc.fxIncomplete,
  };
}

// ─────────────────────── 主函数 ───────────────────────

export function composeNetWorth(input: ComposeNetWorthInput): NetWorthResult {
  const { baseCurrency } = input;
  const fxIndex = buildFxIndex(input.fx ?? []);
  const fxWarnings: FxWarning[] = [];
  const notCounted: NotCountedItem[] = [];
  const anomalies: Anomaly[] = [];

  const assets = createAccs(ASSET_BUCKETS);
  const liabilities = createAccs(LIABILITY_BUCKETS);

  const ctx = { baseCurrency, fxIndex, fxWarnings };
  const at = (key: AssetBucketKey) => assets.get(key) as BucketAcc;
  const lt = (key: LiabilityBucketKey) => liabilities.get(key) as BucketAcc;

  /** 记账一笔「同币种」值 → 目标的折算（所有账户路径共用） */
  const put = (acc: BucketAcc, id: string, amount: Cents, currency: string) =>
    pushLine(acc, id, amount, currency, ctx);

  // ── ① 账户路径 ──
  for (const acc of input.accounts) {
    // 铁律 6：`hidden` 在函数内处理
    if (acc.hidden) continue;

    const raw = acc.balance as number;
    // 余额为 0 不产生明细与分桶（规格：`internal` 余额 0 不计入）
    if (raw === 0) continue;

    switch (acc.type) {
      case AccountType.Cash:
      case AccountType.Debit:
      case AccountType.Ewallet:
      case AccountType.Investment: {
        // 规格 §12 资产方只定义「余额 > 0 的 cash/debit/ewallet/investment」
        if (raw > 0) {
          if (MONETARY_TYPES.includes(acc.type)) {
            put(at('monetary'), acc.id, toCents(raw) as Cents, acc.currency);
          }
        } else {
          anomalies.push({
            sourceId: acc.id,
            kind: 'negativeMonetaryBalance',
            detail: `${acc.type} 账户余额为负（${raw} 分）；规格资产方仅定义「余额 > 0」，该项未计入净资产`,
          });
        }
        break;
      }

      case AccountType.Credit: {
        // 🔴 成对换分类（`DR-060` §3 · `AC-B2.12`）：
        //   `credit 桶 = min(0, 卡余额 + Σ pending 本金)`，剥离出来的部分进 `installmentPending` 桶
        //   （由 `installmentPendingPrincipal` 估值项承载，两者必须成对 —— 只做一边两个方向都错）。
        // 未传 `installmentPendingByAccount` = 该账本无分期数据（原口径，向后兼容）。
        let pendingForAccount = 0;
        for (const it of input.installmentPendingByAccount ?? []) {
          if (it.accountId === acc.id) pendingForAccount += it.amount as number;
        }
        const adjusted = raw + pendingForAccount;
        if (adjusted > 0) {
          put(at('creditOverpay'), acc.id, toCents(adjusted) as Cents, acc.currency);
        } else if (adjusted < 0) {
          put(lt('creditDue'), acc.id, toCents(Math.abs(adjusted)) as Cents, acc.currency);
        }
        // `adjusted === 0` ⇒ 余额为 0 不产生明细（与"internal 余额 0 不计入"同一条纪律）
        break;
      }

      case AccountType.Loan: {
        // 负债方第 2 类：贷款账户余额（与 `contra(loan)` 合并为一类）
        // 符号约定：无论账户以正/负记未偿本金，负债侧取绝对值（Q 域细化 subtype 时确认）
        put(lt('loanBalance'), acc.id, toCents(Math.abs(raw)) as Cents, acc.currency);
        break;
      }

      case AccountType.Internal: {
        const cat = acc.category as InternalCategory | null;
        if (cat === null) {
          anomalies.push({
            sourceId: acc.id,
            kind: 'unknownInternalCategory',
            detail: 'internal 账户缺少 category',
          });
          break;
        }
        // 铁律 3：唯一入表例外
        if (cat === InternalCategory.InsuranceSav) {
          notCounted.push({
            sourceId: acc.id,
            category: cat,
            originalCents: toCents(Math.abs(raw)) as Cents,
            currency: acc.currency,
            reason: 'insuranceSavExcluded',
          });
          break;
        }
        if (SOCIAL_EXCLUDED.includes(cat)) {
          notCounted.push({
            sourceId: acc.id,
            category: cat,
            originalCents: toCents(Math.abs(raw)) as Cents,
            currency: acc.currency,
            reason: 'pensionExcluded',
          });
          break;
        }
        if (SOCIAL_AVAILABLE.includes(cat)) {
          // 可用型社保恒入资产方（余额 > 0 才计入；负值属数据异常）
          if (raw > 0) {
            put(at('socialAvailable'), acc.id, toCents(raw) as Cents, acc.currency);
          } else {
            anomalies.push({
              sourceId: acc.id,
              kind: 'negativePositiveOnlyCategory',
              detail: `${cat} 余额为负（${raw} 分）；可用型社保恒入资产方`,
            });
          }
          break;
        }
        if (cat === InternalCategory.Prepaid) {
          // 预付卡：仅正、无周期 → 计入资产第 1 类（货币资金）
          if (raw > 0) {
            put(at('monetary'), acc.id, toCents(raw) as Cents, acc.currency);
          } else {
            anomalies.push({
              sourceId: acc.id,
              kind: 'negativePositiveOnlyCategory',
              detail: `prepaid 为仅正品类，余额为负（${raw} 分）`,
            });
          }
          break;
        }
        if (cat === InternalCategory.Amortize) {
          // 待摊：仅正 + 有周期 → 资产第 4 类
          if (raw > 0) {
            put(at('amortizing'), acc.id, toCents(raw) as Cents, acc.currency);
          } else {
            anomalies.push({
              sourceId: acc.id,
              kind: 'negativePositiveOnlyCategory',
              detail: `amortize 为仅正品类，余额为负（${raw} 分）`,
            });
          }
          break;
        }
        if (cat === InternalCategory.Payable) {
          // 待缴：可负 → 净额入表（> 0 资产第 5 类 / < 0 负债第 5 类）
          if (raw > 0) {
            put(at('payablePositive'), acc.id, toCents(raw) as Cents, acc.currency);
          } else {
            put(lt('payableNegative'), acc.id, toCents(Math.abs(raw)) as Cents, acc.currency);
          }
          break;
        }
        anomalies.push({
          sourceId: acc.id,
          kind: 'unknownInternalCategory',
          detail: `未知 internal 品类：${cat}`,
        });
        break;
      }

      case AccountType.Contra: {
        const kind = acc.category as ContraKind | null;
        if (kind === null) {
          anomalies.push({
            sourceId: acc.id,
            kind: 'unknownContraKind',
            detail: 'contra 账户缺少 kind（`category` 列）',
          });
          break;
        }
        if (CONTRA_ASSET_KINDS.includes(kind)) {
          // 应收 / 垫付 / 报销剩余本金 → 资产第 3 类
          put(at('receivable'), acc.id, toCents(Math.abs(raw)) as Cents, acc.currency);
          break;
        }
        if (kind === ContraKind.Payable) {
          put(lt('payableRemaining'), acc.id, toCents(Math.abs(raw)) as Cents, acc.currency);
          break;
        }
        if (kind === ContraKind.Loan) {
          // 🔴 与 `loan` 账户**合并为负债第 2 类**（`DR-026`：原 7 类系重复计）
          put(lt('loanBalance'), acc.id, toCents(Math.abs(raw)) as Cents, acc.currency);
          break;
        }
        anomalies.push({
          sourceId: acc.id,
          kind: 'unknownContraKind',
          detail: `未知 contra kind：${kind}`,
        });
        break;
      }

      default: {
        // 穷举保护：`AccountType` 新增值时 TS 会在编译期报错（`never` 检查）
        const exhaustive: never = acc.type;
        throw new Error(`未覆盖的账户类型：${String(exhaustive)}`);
      }
    }
  }

  // ── ② 估值项路径（统一过滤 hidden / 占位行） ──
  const eachValuation = (
    items: readonly ValuationItem[] | undefined,
    bucket: BucketAcc,
    opts: { liability?: boolean } = {},
  ) => {
    for (const it of items ?? []) {
      if (it.hidden) continue;
      if (it.isPlaceholder) continue; // 铁律 6：占位行在函数内剔除（J 域 E5）
      const raw = it.amount as number;
      if (raw === 0) continue;
      if (opts.liability && raw < 0) {
        anomalies.push({
          sourceId: it.id,
          kind: 'negativeLiabilityAmount',
          detail: `负债类估值项传了负数（${raw} 分）；已取绝对值计入`,
        });
      }
      put(bucket, it.id, toCents(Math.abs(raw)) as Cents, it.currency);
    }
  };

  eachValuation(input.holdings, at('holdingsValue'));
  eachValuation(input.majorAssets, at('majorAsset'));
  eachValuation(input.manualAssets, at('manualAsset'));
  eachValuation(input.policyCashValues, at('policyCashValue'));
  eachValuation(input.manualLiabilities, lt('manualLiability'), { liability: true });
  eachValuation(input.installmentPendingPrincipal, lt('installmentPending'), { liability: true });

  // ── ③ 收口 ──
  const assetBuckets = ASSET_BUCKETS.map((d) => toBucket(d, at(d.key)));
  const liabilityBuckets = LIABILITY_BUCKETS.map((d) => toBucket(d, lt(d.key)));

  const totalAssets = sumCents(assetBuckets.map((b) => b.baseCents));
  const totalLiabilities = sumCents(liabilityBuckets.map((b) => b.baseCents));

  return {
    baseCurrency,
    assets: assetBuckets,
    liabilities: liabilityBuckets,
    totalAssets,
    totalLiabilities,
    netWorth: toCents((totalAssets as number) - (totalLiabilities as number)) as Cents,
    fxWarnings,
    notCounted,
    anomalies,
  };
}
